import type {
  GetQBankStructureLevelItemsByQBankStructureIdQuery,
  GetQBankStructureLevelsByQBankStructureIdQuery,
} from '@exam-management/graphql';
import type { QBankStructureLevelItemWithLevel } from '@exam-management/qbs-provider';
import { ErrorBox, Loading } from '@shared/components';
import { useFormCheckbox } from '@shared/hooks';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useMemo } from 'react';

import { formatQBankStructureData, getCheckboxData } from './utils';

interface QBSLevelItemSelectionContextType {
  firstLevelItems: QBankStructureLevelItemWithLevel[];
  data: Map<string, QBankStructureLevelItemWithLevel[]>;
  getCheckboxState: ReturnType<typeof useFormCheckbox>['getCheckboxState'];
  updateCheckbox: ReturnType<typeof useFormCheckbox>['updateCheckboxState'];
  selectAll: ReturnType<typeof useFormCheckbox>['selectAll'];
}

export const QBSLevelItemSelectionContext = createContext<
  QBSLevelItemSelectionContextType | undefined
>(undefined);

interface QBSLevelItemSelectionProviderProps extends PropsWithChildren {
  levels: GetQBankStructureLevelsByQBankStructureIdQuery['qbsGetQBankStructureLevelsByQBankStructureId'];
  levelItems: GetQBankStructureLevelItemsByQBankStructureIdQuery['qbsGetQBankStructureLevelItemsByQBankStructureId'];
  loading?: boolean;
}

export function QBSLevelItemSelectionProvider(props: Readonly<QBSLevelItemSelectionProviderProps>) {
  const { children, levels, levelItems, loading: isLoading } = props;

  const { firstLevelItems, levelItems: sortQBankStructureLevelItems } = useMemo(
    () => formatQBankStructureData(levels, levelItems),
    [levelItems, levels],
  );

  const checkboxHooks = useFormCheckbox({
    data: getCheckboxData(sortQBankStructureLevelItems),
    options: {
      prefix: 'levelItems',
    },
  });

  const values: QBSLevelItemSelectionContextType = useMemo(() => {
    return {
      firstLevelItems,
      data: sortQBankStructureLevelItems,
      getCheckboxState: checkboxHooks.getCheckboxState,
      updateCheckbox: checkboxHooks.updateCheckboxState,
      selectAll: checkboxHooks.selectAll,
    };
  }, [checkboxHooks, firstLevelItems, sortQBankStructureLevelItems]);

  if (isLoading) {
    return <Loading />;
  }

  if (!sortQBankStructureLevelItems) {
    return <ErrorBox />;
  }

  return (
    <QBSLevelItemSelectionContext.Provider value={values}>
      {children}
    </QBSLevelItemSelectionContext.Provider>
  );
}
